import { useEffect, useMemo } from 'react';
import { useDefaultStore } from '@/stores/defaultStore';
import { PageStatus, type PageType } from '@/types/store';
import { useFunnelDetail } from '../queries/useFunnelQueries';
import { useFunnelEditorStore } from '../stores/funnelEditorStore';
import { toFunnelPageItem } from '../logic';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';
import type { FunnelPageItem, FunnelPageProductInfo } from '../types';

const FUNNEL_PAGE_TYPE = 'funnel-page' as PageType;

/**
 * Fetches the funnel detail + derives the pages shown in the editor:
 * - `savedFunnelPages`: server-backed pages, normalized to sequential `order`.
 * - `funnelPages`: `savedFunnelPages` + an optional local-only draft slot.
 * - `productInfoByPageId`: title / price / image resolved against the linksite store.
 *
 * Also owns the first-load "auto-create an initial draft when the funnel is empty" effect.
 */
export function useFunnelEditorData(storeId: number | undefined) {
    const { data: funnel, isLoading } = useFunnelDetail(storeId);
    const { store: linksiteStore } = useDefaultStore();

    const hasDraftPage = useFunnelEditorStore(s => s.hasDraftPage);
    const hasAutoCreatedInitialDraft = useFunnelEditorStore(s => s.hasAutoCreatedInitialDraft);
    const setHasDraftPage = useFunnelEditorStore(s => s.setHasDraftPage);
    const setHasAutoCreatedInitialDraft = useFunnelEditorStore(s => s.setHasAutoCreatedInitialDraft);
    const reset = useFunnelEditorStore(s => s.reset);

    // Reset editor UI state on mount / storeId change and when the editor unmounts.
    useEffect(() => {
        reset();
        return () => reset();
    }, [storeId, reset]);

    const savedFunnelPages: FunnelPageItem[] = useMemo(() => {
        if (!funnel?.pages) return [];
        const sorted = funnel.pages.map(toFunnelPageItem).sort((a, b) => a.order - b.order || a.page_id - b.page_id);
        return sorted.map((p, idx) => ({ ...p, order: idx + 1 }));
    }, [funnel?.pages]);

    useEffect(() => {
        if (isLoading) return;
        if (hasAutoCreatedInitialDraft) return;
        if (savedFunnelPages.length === 0 && !hasDraftPage) {
            setHasDraftPage(true);
        }
        setHasAutoCreatedInitialDraft(true);
    }, [
        isLoading,
        savedFunnelPages.length,
        hasDraftPage,
        hasAutoCreatedInitialDraft,
        setHasDraftPage,
        setHasAutoCreatedInitialDraft,
    ]);

    // Draft takes the next free slot after the highest saved page order.
    const maxSavedOrder = useMemo(
        () => savedFunnelPages.reduce((max, p) => Math.max(max, p.order), 0),
        [savedFunnelPages]
    );

    const draftPage: FunnelPageItem | null = useMemo(() => {
        if (!hasDraftPage) return null;
        const draftOrder = maxSavedOrder + 1;
        const maxRawSavedOrder = savedFunnelPages.reduce((max, p) => Math.max(max, p.rawOrder ?? p.order), 0);
        return {
            page_id: DRAFT_FUNNEL_PAGE_ID,
            title: `Page ${draftOrder}`,
            type: FUNNEL_PAGE_TYPE,
            status: PageStatus.DRAFT,
            order: draftOrder,
            rawOrder: maxRawSavedOrder + 1,
        };
    }, [hasDraftPage, maxSavedOrder, savedFunnelPages]);

    const funnelPages: FunnelPageItem[] = useMemo(
        () => (draftPage ? [...savedFunnelPages, draftPage] : savedFunnelPages),
        [savedFunnelPages, draftPage]
    );

    // V2 pages resolve via referencePageId; legacy pages use inline data; draft has none.
    const productInfoByPageId = useMemo<Record<number, FunnelPageProductInfo | undefined>>(() => {
        const linksitePagesById = new Map((linksiteStore?.pages ?? []).map(p => [p.page_id, p]));

        return Object.fromEntries(
            funnelPages.map(page => {
                if (page.referencePageId) {
                    const referenced = linksitePagesById.get(page.referencePageId);
                    if (referenced) {
                        const info: FunnelPageProductInfo = {
                            title: referenced.data?.product?.title?.trim() || 'Untitled Product',
                            type: referenced.type,
                            price: referenced.data?.product?.price?.amount,
                            currency: referenced.data?.product?.price?.currency,
                            image: referenced.data?.product?.image,
                            description: referenced.data?.product?.description,
                        };
                        return [page.page_id, info];
                    }
                }

                if (page.type !== FUNNEL_PAGE_TYPE) {
                    const info: FunnelPageProductInfo = {
                        title: page.title,
                        type: page.type,
                        price: page.price,
                        currency: page.currency,
                        image: page.image,
                        description: page.description,
                    };
                    return [page.page_id, info];
                }

                return [page.page_id, undefined];
            })
        );
    }, [funnelPages, linksiteStore?.pages]);

    return {
        funnel,
        isLoading,
        savedFunnelPages,
        funnelPages,
        productInfoByPageId,
        maxSavedOrder,
    };
}
